import type { Database } from '@/database.types';
import type { Plan, PlanCreate, PlanUpdate } from '@/entities/plan';
import { planSchema } from '@/entities/plan/schema';
import type { HigherPeriod, Period } from '@/entities/types';

type ApiPlanInsert = Omit<
  Database['public']['Tables']['plans']['Insert'],
  'user_id'
> & {
  user_id?: string;
};

type ApiPlanUpdate = Database['public']['Tables']['plans']['Update'];

function toApiPlanCreate(planCreate: PlanCreate): ApiPlanInsert {
  return {
    id: planCreate.id,
    period: planCreate.period,
    start_at: planCreate.startAt.toISOString(),
    start_at_local: planCreate.startAtLocal,
  };
}

function toApiPlanUpdate(planUpdate: PlanUpdate): ApiPlanUpdate {
  return {
    status: planUpdate.status,
  };
}

function toPlan(apiPlan: Database['public']['Tables']['plans']['Row']): Plan {
  const rawPlan: Plan = {
    id: apiPlan.id,
    createdAt: new Date(apiPlan.created_at),
    updatedAt: new Date(apiPlan.updated_at),
    period: apiPlan.period as Period,
    startAtLocal: apiPlan.start_at_local,
    startAt: new Date(apiPlan.start_at),
    status: apiPlan.status as Plan['status'],
  };

  return planSchema.parse(rawPlan);
}

export function createPlansService(
  supabaseClient: typeof import('./supabase').supabaseClient,
  getUserId: () => string,
) {
  return {
    createPlan: async (planCreate: PlanCreate): Promise<Plan> => {
      const userId = getUserId();
      const apiPlanCreate = toApiPlanCreate(planCreate);

      const { data, error } = await supabaseClient
        .from('plans')
        .insert({ ...apiPlanCreate, user_id: userId })
        .select('*')
        .single();

      if (error) {
        throw error;
      }

      return toPlan(data);
    },

    readPlanForPeriod: async (
      period: Period,
      startAtLocal: string,
    ): Promise<Plan | null> => {
      const userId = getUserId();

      const { data, error } = await supabaseClient
        .from('plans')
        .select('*')
        .eq('user_id', userId)
        .eq('period', period)
        .eq('start_at_local', startAtLocal)
        .maybeSingle();

      if (error) {
        throw error;
      }

      return data ? toPlan(data) : null;
    },

    updatePlan: async (
      planId: string,
      planUpdate: PlanUpdate,
    ): Promise<Plan> => {
      const { data, error } = await supabaseClient
        .from('plans')
        .update(toApiPlanUpdate(planUpdate))
        .eq('id', planId)
        .select('*')
        .single();

      if (error) {
        throw error;
      }

      return toPlan(data);
    },

    readPlannedPlansByPeriods: async (
      checks: Array<{ period: HigherPeriod; startAtLocal: string }>,
    ): Promise<HigherPeriod[]> => {
      const userId = getUserId();

      const orFilter = checks
        .map(
          ({ period, startAtLocal }) =>
            `and(period.eq.${period},start_at_local.eq.${startAtLocal})`,
        )
        .join(',');

      const { data, error } = await supabaseClient
        .from('plans')
        .select('period, planned_goals!inner(goal_id, goals!inner(title))')
        .eq('user_id', userId)
        .or(orFilter)
        .neq('planned_goals.goals.title', '');

      if (error) {
        throw error;
      }

      return [...new Set(data.map((p) => p.period))] as HigherPeriod[];
    },
  };
}
